/**
 * Collection ranking for search — the Searchanise instant search widget and the
 * collections tab on /search.
 *
 * The catalogue has many collections sharing one title ("Shampoo" five times,
 * under Haarpflege, Nature, Men, …), and Searchanise offers no way to say which
 * of them matters most. So the order is corrected here, from the metaobject
 * `[Search] Kollektions-Ranking` (type search_category_ranking), served as JSON
 * by sections/search-category-ranking.liquid. Each entry is a term plus its
 * collections, most important first.
 *
 * Order, in priority:
 *   1. the query is exactly a ranked term (case, spaces and hyphens ignored):
 *      that term's collections go first, in rank order
 *   2. otherwise collections of one title stay where Searchanise put the first
 *      of them, and inside that group the rank decides — unranked ones last
 *   3. everything else keeps Searchanise's relevance order
 * Rule 2 is what makes half-typed queries work: "sham" already pulls the
 * rank-1 "Shampoo" ahead of the other four.
 *
 * Every collection in the ranking also gets a label from its
 * custom.category_path, cut to root and leaf so it fits the widget:
 *   "Haarpflege > Waschen & Pflegen > Shampoo"  →  "Haarpflege > Shampoo"
 * Otherwise five rows reading "Shampoo" are indistinguishable.
 *
 * Re-sorting only reorders what Searchanise returned, so the widget asks for
 * the API's maximum of 50 collections and still shows only as many as its own
 * AutocompleteCategoriesCount setting allows.
 *
 * How the widget is hooked (snize-autocomplete.41671.min.js, checked
 * 2026-10-01): around each getwidgets request it fires two synchronous jQuery
 * events on document, passing the request params and the response by reference:
 *   Searchanise.DataRequestBefore  [type, params]            — before the URL is built
 *   Searchanise.DataRequestEnd     [type, params, response]  — before rendering
 * Neither shows up on a native addEventListener (the payload only travels with
 * jQuery's trigger), so they are bound through Searchanise.$. The widget sets
 * categoriesMaxResults from its own options on every keystroke, which is why
 * Searchanise.SetParams cannot raise it — the Before event can.
 *
 * The widget caches responses by URL and hands the cached object to
 * DataRequestEnd again, so a response is marked once processed: ranking twice
 * would group by labels instead of titles.
 *
 * The link and category_id of a row are untouched, so clicks and Searchanise's
 * click tracking still point at the right collection. One visible loss: the
 * widget only bolds the typed part of a title it starts with, so labelled rows
 * render without the highlight.
 *
 * Without data — definition missing, no entries, endpoint failing — everything
 * here is a no-op and both surfaces show Searchanise's order unchanged.
 *
 * Exposes window.SearchCategoryRanking for assets/searchanise-facets.js:
 *   load()                   Promise, resolves once the ranking is known
 *   isActive()               true when there is anything to rank by
 *   apply(categories, query) ranked, labelled copies
 */
(function () {
  if (window.SearchCategoryRanking) return;

  const SECTION_ID = 'search-category-ranking';
  const STORAGE_KEY = 'search-category-ranking';
  // Ranking edits in the admin reach a visitor at most this late.
  const STORAGE_TTL = 30 * 60 * 1000;
  // The API's ceiling for categoriesMaxResults.
  const FETCH_CATEGORIES = 50;
  const UNRANKED = Number.MAX_SAFE_INTEGER;
  const MARK = '__searchCategoryRanking';

  const endpoint = document.currentScript?.dataset.endpoint || '/';

  /** Map of normalised term → collection ids, most important first. */
  let byTerm = new Map();
  /** Map of collection id → display label. */
  let labels = new Map();
  let loading = null;

  const normalize = (value) =>
    String(value || '')
      .toLowerCase()
      .replace(/[\s-]+/g, ' ')
      .trim();

  /** Root and leaf of a breadcrumb; shorter paths stay as they are. */
  const shorten = (path) => {
    const parts = String(path || '')
      .split('>')
      .map((part) => part.trim())
      .filter(Boolean);
    return parts.length > 2 ? `${parts[0]} > ${parts[parts.length - 1]}` : parts.join(' > ');
  };

  const rankOf = (ids, id) => {
    const index = ids ? ids.indexOf(id) : -1;
    return index < 0 ? UNRANKED : index;
  };

  function index(data) {
    byTerm = new Map();
    labels = new Map();

    for (const { term, collections } of data.terms || []) {
      const key = normalize(term);
      if (!key) continue;
      // "Make Up" and "Make-Up" normalise to one key — their lists are merged.
      const ids = byTerm.get(key) || [];
      for (const collection of collections || []) {
        const id = String(collection.id);
        if (!ids.includes(id)) ids.push(id);
        labels.set(id, shorten(collection.path));
      }
      byTerm.set(key, ids);
    }

    if (data.truncated) {
      console.warn(
        `[search-category-ranking] more than 250 ranking entries — only the first 250 are used, see sections/${SECTION_ID}.liquid`
      );
    }
  }

  function readStored() {
    try {
      const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
      return stored && Date.now() - stored.at < STORAGE_TTL ? stored.data : null;
    } catch (error) {
      return null;
    }
  }

  function store(data) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ at: Date.now(), data }));
    } catch (error) {
      // Private mode or a full quota: the next page simply fetches again.
    }
    return data;
  }

  async function fetchRanking() {
    const url = new URL(endpoint, window.location.origin);
    url.searchParams.set('section_id', SECTION_ID);

    const response = await fetch(url.toString());
    if (!response.ok) throw new Error(`ranking endpoint responded ${response.status}`);

    const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
    const node = parsed.querySelector('[data-search-category-ranking]');
    // No node: the definition does not exist yet (Liquid cannot paginate nil and
    // renders an error instead). Treated as an empty ranking, not as a failure.
    return node ? JSON.parse(node.textContent) : { terms: [] };
  }

  function load() {
    if (!loading) {
      const stored = readStored();
      loading = (stored ? Promise.resolve(stored) : fetchRanking().then(store))
        .then(index)
        .catch((error) => console.warn('[search-category-ranking]', error));
    }
    return loading;
  }

  const isActive = () => byTerm.size > 0;

  /** Ranked copies of the Searchanise categories, titles replaced by labels. */
  function apply(categories, query) {
    if (!isActive() || !Array.isArray(categories)) return categories;

    const pinned = byTerm.get(normalize(query));
    const groupStart = new Map();

    return categories
      .map((category, position) => {
        const id = String(category.category_id);
        const key = normalize(category.title);
        if (!groupStart.has(key)) groupStart.set(key, position);

        return {
          category,
          position,
          pin: rankOf(pinned, id),
          group: groupStart.get(key),
          rank: rankOf(byTerm.get(key), id),
        };
      })
      .sort((a, b) => a.pin - b.pin || a.group - b.group || a.rank - b.rank || a.position - b.position)
      .map(({ category }) => {
        const label = labels.get(String(category.category_id));
        return label ? { ...category, title: label } : category;
      });
  }

  let bound = false;

  function bindWidget() {
    const $ = window.Searchanise?.$;
    if (bound || !$) return;
    bound = true;

    $(document).on('Searchanise.DataRequestBefore', (event, type, params) => {
      if (type !== 'autocomplete' || !params || !isActive()) return;
      // 0 means the panel hides collections altogether — leave that alone.
      if (Number(params.categoriesMaxResults) > 0) params.categoriesMaxResults = FETCH_CATEGORIES;
    });

    $(document).on('Searchanise.DataRequestEnd', (event, type, params, response) => {
      if (type !== 'autocomplete' || !response || response[MARK] || !isActive()) return;
      if (!Array.isArray(response.categories)) return;

      response.categories = apply(response.categories, params?.q);
      response[MARK] = true;
    });
  }

  window.SearchCategoryRanking = { load, isActive, apply };

  // Searchanise.Loaded fires once its jQuery is in place and before the widget
  // binds the inputs, so binding here catches the very first request.
  if (window.Searchanise?.Loaded) bindWidget();
  else document.addEventListener('Searchanise.Loaded', bindWidget);

  // The widget's events are synchronous and cannot wait for a fetch, so the
  // ranking has to be in place before the first keystroke. Idle time is early
  // enough, and from sessionStorage it costs nothing.
  const idle = window.requestIdleCallback || ((callback) => setTimeout(callback, 1));
  idle(() => load());
})();
